import {Controller, useFormContext, useWatch} from 'react-hook-form';
import AppButton from '../../../../../../../components/common/AppButton';
import {SVG} from '../../../../../../../constants/assetLists';
import {
  ChecklistFormValues,
  ChecklistSidebarSelection,
  FolderValues,
  GENERAL_FOLDER_NAME,
  isGeneralChecklistFolderId,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../../types';
import ChecklistSidebar from './ChecklistSidebar';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppMenu from '../../../../../../../components/common/AppMenu';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AddEditCheckList, {DepartmentOptionType} from './AddEditCheckList';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import {
  downloadOnboardingChecklistExcelExport,
  readOnboardingChecklistExcelFile,
} from './checklistExcelTemplate';
import {DepartmentFormValues} from '../../../types';
import MUIIconCheckboxSelect from '../../../../../../../components/common/MUIIconCheckboxSelect';
import {Chip} from '@mui/material';
import {ICONS} from '../../../../../../users/data';
import DuplicateFolderDialog from './DuplicateFolderDialog';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

const MENUITEMS = [
  {
    title: 'עריכה',
    value: 'edit',
    icon: SVG.editGrayIcon,
  },
  {
    title: 'שכפול',
    value: 'duplicate',
    icon: SVG.copyGrayIcon,
  },
  {
    title: 'יצוא',
    value: 'export',
    icon: SVG.excelGreenIcon,
  },
];

function getDepartmentsSelectValue(
  selected: string[],
  departmentOptions: DepartmentOptionType[],
): DepartmentOptionType[] {
  if (!departmentOptions.length) return [];

  const selectedIds =
    selected?.[0] === 'all' ? departmentOptions.map(o => o.value) : selected;

  return departmentOptions.filter(o => selectedIds.includes(o.value));
}

function filterChecklistsForSelection(
  checklists: ChecklistFormValues[],
  selection: ChecklistSidebarSelection | null,
  checklistFolders: FolderValues[],
) {
  if (!selection) return [];

  if (
    selection.type === 'folder' &&
    isGeneralChecklistFolderId(selection.id, checklistFolders)
  ) {
    return checklists.map((item, globalIndex) => ({item, globalIndex}));
  }

  return checklists
    .map((item, globalIndex) => ({item, globalIndex}))
    .filter(({item}) =>
      selection.type === 'shift'
        ? item.shiftId === selection.id
        : item.folderId === selection.id,
    );
}

function Checklists({selectedFieldPath}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [selectedSidebarItem, setSelectedSidebarItem] =
    useState<ChecklistSidebarSelection | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState<string | null>(
    null,
  );
  const [listRevision, setListRevision] = useState(0);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const {showSnackbar} = useSnackbar();
  const excelInputRef = useRef<HTMLInputElement>(null);

  const {control, watch, getValues, setValue} =
    useFormContext<NetworkDetailsFormValues>();

  const checklistsPath = `${selectedFieldPath}.checklists` as const;

  const shifts = watch(`${selectedFieldPath}.shifts`) || [];
  const checklistFolders = watch(`${selectedFieldPath}.checklistFolders`) || [];
  const checklists = useWatch({control, name: checklistsPath}) ?? [];
  const nodeDepartments = watch(`${selectedFieldPath}.departments`) ?? [];

  const departmentOptions = useMemo((): DepartmentOptionType[] => {
    if (nodeDepartments.length === 0) return [];

    const dept = [
      {name: 'כל המחלקות', id: 'all', icon: SVG.saveAllIcon},
      ...nodeDepartments,
    ];

    return dept.map(department => {
      const deptIcon =
        ICONS?.find(icon => icon.id === department.icon)?.icon ||
        SVG.saveAllIcon;

      return {
        label: department.name,
        value: department.id,
        icon: deptIcon,
      };
    });
  }, [nodeDepartments]);

  useEffect(() => {
    const isShiftSelected =
      selectedSidebarItem?.type === 'shift' &&
      shifts.some(s => s.id === selectedSidebarItem.id);
    const isFolderSelected =
      selectedSidebarItem?.type === 'folder' &&
      checklistFolders.some(f => f.id === selectedSidebarItem.id);

    if (isShiftSelected || isFolderSelected) return;

    if (shifts.length > 0) {
      setSelectedSidebarItem({type: 'shift', id: shifts[0].id});
    } else if (checklistFolders.length > 0) {
      setSelectedSidebarItem({type: 'folder', id: checklistFolders[0].id});
    } else {
      setSelectedSidebarItem(null);
    }
  }, [selectedFieldPath, shifts, checklistFolders, selectedSidebarItem]);

  const getChecklistCount = useCallback(
    (type: 'shift' | 'folder', id: string) => {
      if (type === 'folder' && isGeneralChecklistFolderId(id, checklistFolders)) {
        return checklists.length;
      }

      return checklists.filter(item =>
        type === 'shift' ? item.shiftId === id : item.folderId === id,
      ).length;
    },
    [checklists, checklistFolders],
  );

  const visibleChecklists = useMemo(
    () =>
      filterChecklistsForSelection(
        checklists,
        selectedSidebarItem,
        checklistFolders,
      ),
    [checklists, selectedSidebarItem, checklistFolders],
  );

  const headerTitle = useMemo(() => {
    if (!selectedSidebarItem) return '';

    if (selectedSidebarItem.type === 'shift') {
      const shift = shifts.find(s => s.id === selectedSidebarItem.id);
      if (!shift) return '';

      const timing = shift.from && shift.to ? `${shift.from}-${shift.to}` : '';
      return timing ? `${shift.name} ${timing}` : shift.name;
    }

    if (isGeneralChecklistFolderId(selectedSidebarItem.id, checklistFolders)) {
      return GENERAL_FOLDER_NAME;
    }

    const folder = checklistFolders.find(f => f.id === selectedSidebarItem.id);
    return folder?.name ?? '';
  }, [selectedSidebarItem, shifts, checklistFolders]);

  const handleAddChecklist = () => {
    if (!selectedSidebarItem) return;
    setEditingChecklistId(null);
    setDialogOpen(true);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    globalIndex: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedIdx(globalIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedIdx(null);
  };

  const handleItemClick = (value: string) => {
    if (selectedIdx === null) {
      handleMenuClose();
      return;
    }

    const checklist = checklists[selectedIdx];

    if (value === 'edit' && checklist) {
      setEditingChecklistId(checklist.id);
      setDialogOpen(true);
    } else if (value === 'duplicate' && checklist) {
      setEditingChecklistId(checklist.id);
      setShowDuplicateDialog(true);
    } else if (value === 'export' && checklist) {
      const nodeDepartments =
        (getValues(
          `${selectedFieldPath}.departments`,
        ) as DepartmentFormValues[]) ?? [];

      downloadOnboardingChecklistExcelExport(checklist, nodeDepartments);
    }

    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingChecklistId(null);
  };

  const handleDuplicateDialogClose = () => {
    setShowDuplicateDialog(false);
    setEditingChecklistId(null);
  };

  const handleImportClick = () => {
    if (!selectedSidebarItem) return;

    excelInputRef.current?.click();
  };

  const handleDepartmentsChange = useCallback(
    (
      e: React.SyntheticEvent,
      value: DepartmentOptionType[] | null,
      globalIndex: number,
    ) => {
      const path =
        `${selectedFieldPath}.checklists.${globalIndex}.departments` as const;
      const currentDepartments = getValues(path) ?? [];

      const text = (e.currentTarget as HTMLElement).textContent;
      const isAllSelectedByText = text === 'כל המחלקות';
      const selectedValues = value?.map(item => item.value) ?? [];
      const allDeptSelected = nodeDepartments.every(dept =>
        selectedValues.includes(dept.id),
      );
      const hasAllInSelection = selectedValues.includes('all');
      const hadAllPreviously = currentDepartments.includes('all');

      let newValue: string[] = [];
      if (hadAllPreviously && !hasAllInSelection) {
        newValue = [];
      } else if (hadAllPreviously) {
        newValue = selectedValues.filter(v => v !== 'all');
      } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
        newValue = ['all'];
      } else {
        newValue = selectedValues;
      }

      setValue(path, newValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [selectedFieldPath, getValues, setValue, nodeDepartments],
  );

  const handleRemoveChecklist = useCallback(
    (globalIndex: number) => {
      const current = getValues(checklistsPath) ?? [];
      setValue(
        checklistsPath,
        current.filter((_, i) => i !== globalIndex),
        {shouldValidate: true, shouldDirty: true},
      );
    },
    [checklistsPath, getValues, setValue],
  );

  const handleExcelPicked: React.ChangeEventHandler<
    HTMLInputElement
  > = async e => {
    const file = e.target.files?.[0];
    if (!file || !selectedSidebarItem) return;

    const nodeDepartments =
      (getValues(
        `${selectedFieldPath}.departments`,
      ) as DepartmentFormValues[]) ?? [];

    try {
      const {checklists: imported, rowErrors} =
        await readOnboardingChecklistExcelFile(
          file,
          nodeDepartments,
          selectedSidebarItem,
        );

      if (imported.length === 0) {
        const detail =
          rowErrors.length > 0
            ? rowErrors.slice(0, 3).join(' | ')
            : 'לא נמצאו צ’קליסטים תקינים בקובץ';
        showSnackbar({
          type: 'error',
          message: detail,
        });
        return;
      }

      const currentChecklists = getValues(checklistsPath) ?? [];
      setValue(checklistsPath, [...currentChecklists, ...imported], {
        shouldValidate: true,
        shouldDirty: true,
      });

      const successMessage =
        rowErrors.length > 0
          ? `${imported.length} צ’קליסטים יובאו. ${rowErrors.length} שורות נדחו.`
          : `${imported.length} צ’קליסטים יובאו בהצלחה`;

      showSnackbar({
        type: rowErrors.length > 0 ? 'info' : 'success',
        message: successMessage,
      });

      if (rowErrors.length > 0) {
        console.warn('Checklist import row errors:', rowErrors);
      }
    } catch (err) {
      console.error(err);
      showSnackbar({
        type: 'error',
        message: 'שגיאה בקריאת קובץ האקסל',
      });
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="flex-1 min-h-0 flex gap-2">
      <ChecklistSidebar
        selectedFieldPath={selectedFieldPath}
        selectedItem={selectedSidebarItem}
        onSelectShift={id => setSelectedSidebarItem({type: 'shift', id})}
        onSelectFolder={id => setSelectedSidebarItem({type: 'folder', id})}
        getChecklistCount={getChecklistCount}
      />

      <div className="flex-1 min-h-0 p-3 border border-gray70 rounded-lg shadow-sm flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="m-0 text-xl font-bold text-darkBlue">{headerTitle}</p>

          <AppButton
            variant="custom"
            disabled={!selectedSidebarItem}
            onClick={handleImportClick}
            iconStart={
              <img
                src={SVG.addIcon}
                alt="icon"
                className="w-3 h-3 object-contain"
              />
            }
            className={`text-xl font-semibold bg-[#1AC3F2] transition-all duration-200 hover:opacity-90`}>
            יבוא צ’קליסט
          </AppButton>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleExcelPicked}
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
          {visibleChecklists.map(({item, globalIndex}, index) => (
            <div
              key={`${item.id}-${listRevision}`}
              className="flex gap-3 p-2.5 border border-gray70 rounded-lg shadow-sm">
              <span className="text-xl text-gray font-light self-center w-[20px]">
                {String(index + 1).padStart(2, '0')}
              </span>

              <Controller
                control={control}
                name={`${selectedFieldPath}.checklists.${globalIndex}.title`}
                render={({field, fieldState: {error}}) => (
                  <div className="flex-1">
                    <AppTextInput
                      label="שם צ’קליסט"
                      placeholder="שם צ’קליסט"
                      className="border-none"
                      {...field}
                      error={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name={`${selectedFieldPath}.checklists.${globalIndex}.departments`}
                control={control}
                render={({field: {value}, fieldState: {error}}) => {
                  const departments = value ?? [];
                  const departmentsValue = getDepartmentsSelectValue(
                    departments,
                    departmentOptions,
                  );

                  return (
                    <div className="flex-1">
                      <MUIIconCheckboxSelect
                        label="משוייך למחלקה/ות"
                        options={departmentOptions}
                        size="small"
                        fullWidth
                        disableCloseOnSelect
                        disabled={departmentOptions.length === 0}
                        id={`checklist-departments-${globalIndex}`}
                        renderValue={(selectedOptions, getTagProps) => {
                          const allSelected =
                            departments[0] === 'all' ||
                            selectedOptions?.length ===
                              departmentOptions.length;

                          const renderChip = (
                            option: DepartmentOptionType,
                            index = 0,
                          ) => (
                            <Chip
                              label={option.label}
                              icon={
                                <img
                                  src={option.icon}
                                  alt="icon"
                                  className="w-[18px] h-[18px] object-contain"
                                />
                              }
                              {...getTagProps({index})}
                            />
                          );

                          if (allSelected) {
                            const allOption = departmentOptions.find(
                              o => o.value === 'all',
                            );
                            return allOption ? renderChip(allOption) : null;
                          }

                          return selectedOptions.map((option, index) =>
                            renderChip(option, index),
                          );
                        }}
                        value={departmentsValue}
                        onChange={(e, selected) =>
                          handleDepartmentsChange(e, selected, globalIndex)
                        }
                        error={!!error}
                        helperText={error?.message}
                      />
                    </div>
                  );
                }}
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.checklists.${globalIndex}.description`}
                render={({field, fieldState: {error}}) => (
                  <div className="flex-1">
                    <AppTextInput
                      label="תיאור קצר"
                      placeholder="תיאור קצר"
                      className="border-none"
                      {...field}
                      error={error?.message}
                    />
                  </div>
                )}
              />

              <div className="self-center">
                <AppMenu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedIdx === globalIndex}
                  onOpen={e => handleMenuOpen(e, globalIndex)}
                  onClose={handleMenuClose}
                  items={MENUITEMS}
                  onItemClick={handleItemClick}
                />
              </div>

              <button
                type="button"
                className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
                onClick={() => handleRemoveChecklist(globalIndex)}>
                <img
                  src={SVG.deleteGrayIcon}
                  alt="del"
                  className="w-4 h-[18px] object-contain"
                />
              </button>
            </div>
          ))}

          <div>
            <button
              type="button"
              className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
              onClick={handleAddChecklist}
              disabled={!selectedSidebarItem}>
              <img
                src={SVG.addDarkBlueIcon}
                alt="add"
                className="w-[14px] h-[14px] object-contain"
              />
            </button>
          </div>
        </div>
      </div>

      <AddEditCheckList
        open={dialogOpen}
        onClose={handleDialogClose}
        onSaved={() => setListRevision(revision => revision + 1)}
        selectedFieldPath={selectedFieldPath}
        editingChecklistId={editingChecklistId ?? undefined}
        sidebarSelection={selectedSidebarItem}
      />

      <DuplicateFolderDialog
        open={showDuplicateDialog}
        onClose={handleDuplicateDialogClose}
        onSaved={() => setListRevision(revision => revision + 1)}
        selectedFieldPath={selectedFieldPath}
        editingChecklistId={editingChecklistId ?? undefined}
        sidebarSelection={selectedSidebarItem}
      />
    </div>
  );
}

export default Checklists;
